import { useEffect, useState, useCallback } from 'react';
import * as smartbillSvc from '../../services/smartbill';
import { listProceduri } from '../../services/proceduri';
import { TIP_PROC_LABEL } from './FacturaModal';
import { FilterPill } from '../Shared/FilterPill';
import DateField from './DateField';
import './InvoiceEditModal.css';

const TIP_INCASARE_OPTIONS = [
  'Ordin plata', 'Card', 'Chitanta', 'CEC', 'Bilet ordin', 'Mandat postal', 'Alta incasare',
];

const STATUS_OPTIONS = [
  { v: 'neplatita',         l: 'Neplatita' },
  { v: 'platita',           l: 'Platita' },
  { v: 'facturata',         l: 'Facturata' },
  { v: 'facturata_partial', l: 'Facturata partial' },
  { v: 'depasita',          l: 'Depasita' },
  { v: 'anulata',           l: 'Anulata' },
  { v: 'stornata',          l: 'Stornata' },
];

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtMoney(v, moneda) {
  if (v === null || v === undefined) return '—';
  return `${parseFloat(v).toFixed(2)} ${moneda || 'RON'}`;
}

/**
 * InvoiceEditModal — view + edit local fields of an existing SmartbillDocument.
 *
 * Props:
 *   doc         — the document object (from _sb_doc_to_dict)
 *   onClose     — called when modal closes
 *   onSaved     — called with updated doc after PATCH
 *   onConverted — called with new fiscala doc after inline convert (FacturarePage flow)
 *   onConvert   — if provided, "→ Fiscala" delegates to parent (ClientiPage flow)
 *   showToast   — function(msg)
 */
export default function InvoiceEditModal({ doc: initialDoc, onClose, onSaved, onConverted, onConvert, onReEdit, onDuplicate, showToast }) {
  const [doc, setDoc] = useState(initialDoc);
  const [form, setForm] = useState({
    tip_serviciu:  initialDoc.tip_serviciu  || 'taxe',
    status_plata:  initialDoc.status_plata  || 'neplatita',
    data_scadenta: initialDoc.data_scadenta || '',
    observatii:    initialDoc.observatii    || '',
    procedura_id:  initialDoc.procedura_id  ? String(initialDoc.procedura_id) : '',
  });
  const [proceduri, setProceduri] = useState([]);
  useEffect(() => {
    if (!initialDoc.client_id) return;
    listProceduri(initialDoc.client_id).then(d => setProceduri(d.proceduri || [])).catch(() => {});
  }, [initialDoc.client_id]); // eslint-disable-line
  const [saving,        setSaving]        = useState(false);
  const [downloading,   setDownloading]   = useState(false);
  const [converting,    setConverting]    = useState(false);
  const [convertMeta,   setConvertMeta]   = useState({
    serie:         '',
    data_emitere:  new Date().toISOString().slice(0, 10),
    data_scadenta: initialDoc.data_scadenta || '',
  });
  const [series,        setSeries]        = useState([]);
  const [convertSaving, setConvertSaving] = useState(false);

  // Per-line service type — the only line-item field editable here (local only,
  // never re-sent to SmartBill). Everything else about the produse stays fixed.
  const [produse, setProduse] = useState(() =>
    (initialDoc.produse || []).map(p => ({ ...p })));
  const produseDirty = JSON.stringify(produse) !== JSON.stringify(initialDoc.produse || []);
  const setLineTip = (i, tip) =>
    setProduse(ps => ps.map((p, idx) => idx === i ? { ...p, tip_serviciu: tip } : p));

  // Incasari state
  const [payments,        setPayments]        = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentSummary,  setPaymentSummary]  = useState({ total: 0, total_incasat: 0, rest_de_incasat: 0 });
  const [incasareForm,    setIncasareForm]    = useState({
    valoare: '', tip: 'Ordin plata',
    data_incasare: new Date().toISOString().slice(0, 10),
    observatii: '',
  });
  const [addingIncasare,  setAddingIncasare]  = useState(false);
  const [savingIncasare,  setSavingIncasare]  = useState(false);
  const [stornoLoading,   setStornoLoading]   = useState(false);

  const isProforma = doc.tip === 'proforma';
  const isFiscala  = doc.tip === 'fiscala';
  const isStornata = doc.status_plata === 'stornata' || !!doc.storno_numar;
  const setF  = (p) => setForm(f  => ({ ...f,  ...p }));
  const setCM = (p) => setConvertMeta(m => ({ ...m, ...p }));
  const setIF = (p) => setIncasareForm(f => ({ ...f, ...p }));

  const loadPayments = useCallback(async () => {
    if (!isFiscala || !doc.numar) return;
    setPaymentsLoading(true);
    try {
      const r = await smartbillSvc.getPayments(doc.id);
      setPayments(r.data.payments || []);
      setPaymentSummary({
        total:           r.data.total || 0,
        total_incasat:   r.data.total_incasat || 0,
        rest_de_incasat: r.data.rest_de_incasat || 0,
      });
    } catch {
      // silent — incasarile nu sunt critice
    } finally {
      setPaymentsLoading(false);
    }
  }, [doc.id, isFiscala, doc.numar]);

  useEffect(() => { loadPayments(); }, [loadPayments]);

  useEffect(() => {
    if (!converting) return;
    smartbillSvc.getSeries(doc.companie, 'f')
      .then(r => setSeries(r.data?.list || []))
      .catch(() => setSeries([]));
  }, [converting, doc.companie]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = produseDirty ? { ...form, produse } : form;
      const res = await smartbillSvc.patchDocument(doc.id, payload);
      setDoc(res.data.document);
      onSaved?.(res.data.document);
      showToast('Salvat!');
      onClose();
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!doc.numar || downloading) return;
    setDownloading(true);
    try {
      const res = await smartbillSvc.downloadPdf(doc.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.serie}-${doc.numar}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast('Eroare la descarcare PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handleConvertClick = () => {
    if (onConvert) {
      onConvert(doc);
      onClose();
    } else {
      setConverting(true);
    }
  };

  const handleAddIncasare = async () => {
    if (!incasareForm.valoare || parseFloat(incasareForm.valoare) <= 0) {
      showToast('Introduceti o valoare valida'); return;
    }
    setSavingIncasare(true);
    try {
      await smartbillSvc.addPayment(doc.id, {
        valoare:       parseFloat(incasareForm.valoare),
        tip:           incasareForm.tip,
        data_incasare: incasareForm.data_incasare,
        observatii:    incasareForm.observatii,
      });
      showToast('✓ Incasare inregistrata');
      setAddingIncasare(false);
      setIncasareForm({ valoare: '', tip: 'Ordin plata', data_incasare: new Date().toISOString().slice(0, 10), observatii: '' });
      await loadPayments();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Eroare la inregistrarea incasarii');
    } finally {
      setSavingIncasare(false);
    }
  };

  const handleStorno = async () => {
    if (!window.confirm('Esti sigur ca vrei sa stornezi aceasta factura? Actiunea nu poate fi anulata.')) return;
    setStornoLoading(true);
    try {
      const res = await smartbillSvc.stornoDocument(doc.id);
      setDoc(res.data.document);
      onSaved?.(res.data.document);
      showToast('✓ Factura stornata');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Eroare la stornare');
    } finally {
      setStornoLoading(false);
    }
  };

  const handleConvert = async () => {
    if (!convertMeta.serie) { showToast('Selecteaza seria'); return; }
    setConvertSaving(true);
    try {
      const res = await smartbillSvc.convertToFiscala(doc.id, convertMeta);
      showToast('✓ Factura fiscala emisa!');
      onConverted?.(res.data.document);
      onClose();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Eroare la conversie');
    } finally {
      setConvertSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box iem-modal">

        {/* ── Header ── */}
        <div className="modal-sticky-header iem-header">
          <div className="iem-header-row">
            <div className="iem-header-left">
              <span className={`fin-doc-badge fin-doc-badge--tip${doc.tip === 'fiscala' ? '' : ' fin-doc-badge--proforma'}`}>
                {doc.tip === 'fiscala' ? 'Fiscala' : 'Proforma'}
              </span>
              <span className="iem-doc-nr">{doc.serie}-{doc.numar || '—'}</span>
              <span className="iem-sep">·</span>
              <span className="iem-companie">{doc.companie}</span>
              {doc.client_name && <><span className="iem-sep">·</span><span className="iem-client">{doc.client_name}</span></>}
            </div>
            <button className="icon-circle-btn" onClick={onClose} title="Inchide">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          {doc.proforma_ref_numar && (
            <div className="iem-ref">din proforma {doc.proforma_ref_serie}-{doc.proforma_ref_numar}</div>
          )}
        </div>

        <div className="modal-scroll-body">
          {converting ? (
            /* ── Inline convert-to-fiscala form ── */
            <div className="iem-convert-panel">
              <p className="iem-convert-hint">
                Emite factura fiscala din proforma <strong>{doc.serie}-{doc.numar}</strong>
              </p>
              <div className="f-row f-row-3">
                <div className="field">
                  <label>Serie fiscala</label>
                  <FilterPill value={convertMeta.serie} onChange={e => setCM({ serie: e.target.value })}>
                    <option value="">— Selecteaza —</option>
                    {series.map(s => (
                      <option key={s.name} value={s.name}>
                        {s.name}{s.nextNumber ? ` — nr. ${s.nextNumber}` : ''}
                      </option>
                    ))}
                  </FilterPill>
                </div>
                <div className="field">
                  <label>Data emitere</label>
                  <DateField value={convertMeta.data_emitere} onChange={v => setCM({ data_emitere: v })} />
                </div>
                <div className="field">
                  <label>Data scadenta</label>
                  <DateField value={convertMeta.data_scadenta} onChange={v => setCM({ data_scadenta: v })} />
                </div>
              </div>
            </div>
          ) : (
            /* ── Normal edit view ── */
            <>
              {/* Info strip */}
              <div className="iem-info-strip" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 24 }}>
                  <div className="iem-info-item">
                    <span className="iem-info-label">Emis</span>
                    <span>{fmtDate(doc.data_emitere)}</span>
                  </div>
                  <div className="iem-info-item">
                    <span className="iem-info-label">Moneda</span>
                    <span>{doc.moneda}{doc.curs_valutar ? ` @ ${parseFloat(doc.curs_valutar).toFixed(4)} RON` : ''}</span>
                  </div>
                  <div className="iem-info-item">
                    <span className="iem-info-label">Total</span>
                    <strong className="iem-total-val">{fmtMoney(doc.total, doc.moneda)}</strong>
                  </div>
                </div>
                {doc.numar && (
                  <button
                    type="button"
                    className="icon-circle-btn"
                    onClick={handleDownload}
                    disabled={downloading}
                    title="Descarca PDF"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                  </button>
                )}
              </div>

              {/* Editable fields */}
              <div className="f-row f-row-3" style={{ marginTop: 20 }}>
                <div className="field">
                  <label>Tip serviciu</label>
                  <FilterPill value={form.tip_serviciu} onChange={e => setF({ tip_serviciu: e.target.value })}>
                    <option value="">— Tip serviciu —</option>
                    <option value="taxe">Taxe</option>
                    <option value="onorariu">Onorariu</option>
                    <option value="mixt">Taxe + Onorariu</option>
                  </FilterPill>
                </div>
                <div className="field">
                  <label>Status plata</label>
                  <FilterPill value={form.status_plata} onChange={e => setF({ status_plata: e.target.value })}>
                    <option value="">— Status —</option>
                    {STATUS_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </FilterPill>
                </div>
                <div className="field">
                  <label>Data scadenta</label>
                  <DateField value={form.data_scadenta} onChange={v => setF({ data_scadenta: v })} />
                </div>
              </div>

              {doc.produse?.length > 0 && (
                <div className="field">
                  <label>Detalii</label>
                  <div className="iem-detalii">
                    {doc.produse.map((p, i) => <span key={i}>{p.name}</span>)}
                  </div>
                </div>
              )}

              <div className="field">
                <label>Observatii</label>
                <textarea rows={3} value={form.observatii} onChange={e => setF({ observatii: e.target.value })} />
              </div>

              <div className="field">
                <label>Procedura asociata</label>
                <FilterPill value={form.procedura_id} onChange={e => setF({ procedura_id: e.target.value })}>
                  <option value="">— Fara procedura —</option>
                  {proceduri.map(p => {
                    const t = TIP_PROC_LABEL[p.tip_proc] || p.tip_proc;
                    const nr = p.data?.numar || p.data?.numarDepozit || p.data?.denumire || '';
                    return <option key={p.id} value={String(p.id)}>{nr ? `${t} · ${nr}` : t}</option>;
                  })}
                </FilterPill>
              </div>

              {/* Produse — read-only, except the per-line Tip serviciu (local only) */}
              {produse.length > 0 && (
                <>
                  <div className="section-label" style={{ marginTop: 20 }}>Produse / Servicii</div>
                  {produse.map((p, i) => (
                    <div key={i} className="iem-produs">
                      <div className="iem-produs-left">
                        <span className="iem-produs-name">{p.name}</span>
                        {p.productDescription && <span className="iem-produs-desc">{p.productDescription}</span>}
                      </div>
                      <div className="iem-produs-right">
                        <FilterPill value={p.tip_serviciu || ''} onChange={e => setLineTip(i, e.target.value)}>
                          <option value="">— Tip —</option>
                          <option value="taxe">Taxe</option>
                          <option value="onorariu">Onorariu</option>
                        </FilterPill>
                        <span className="iem-produs-qty">
                          {p.quantity} × {parseFloat(p.price || 0).toFixed(2)} {doc.moneda}
                        </span>
                      </div>
                    </div>
                  ))}
                  <div className="fin-totals">
                    <div className="fin-total-row"><span>Total fara TVA</span><span>{fmtMoney(doc.total_fara_tva, doc.moneda)}</span></div>
                    <div className="fin-total-row"><span>TVA</span><span>{fmtMoney(doc.total_tva, doc.moneda)}</span></div>
                    <div className="fin-total-row fin-total-row--bold"><span>Total</span><span>{fmtMoney(doc.total, doc.moneda)}</span></div>
                  </div>
                </>
              )}

              {/* ── Incasari (fiscal invoices only) ── */}
              {isFiscala && doc.numar && (
                <div className="iem-incasari-section">
                  <div className="iem-incasari-header">
                    <span className="section-label" style={{ margin: 0 }}>Incasari</span>
                    <div className="iem-incasari-summary">
                      <span className="iem-inc-chip iem-inc-chip--incasat">
                        Incasat: {fmtMoney(paymentSummary.total_incasat, doc.moneda)}
                      </span>
                      <span className="iem-inc-chip iem-inc-chip--rest">
                        Rest: {fmtMoney(paymentSummary.rest_de_incasat, doc.moneda)}
                      </span>
                    </div>
                  </div>

                  {paymentsLoading && <div className="iem-inc-empty">Se incarca...</div>}

                  {!paymentsLoading && payments.length === 0 && !addingIncasare && (
                    <div className="iem-inc-empty">Nicio incasare inregistrata.</div>
                  )}

                  {!paymentsLoading && payments.map(p => (
                    <div key={p.id} className="iem-inc-row">
                      <div className="iem-inc-row-left">
                        <span className="iem-inc-tip">{p.tip}</span>
                        <span className="iem-inc-date">{fmtDate(p.data_incasare)}</span>
                        {p.created_by_name && <span className="iem-inc-by">de {p.created_by_name}</span>}
                        {p.observatii && <span className="iem-inc-obs">{p.observatii}</span>}
                      </div>
                      <span className="iem-inc-val">{fmtMoney(p.valoare, doc.moneda)}</span>
                    </div>
                  ))}

                  {addingIncasare ? (
                    <div className="iem-inc-form">
                      <div className="f-row f-row-3">
                        <div className="field">
                          <label>Valoare</label>
                          <input
                            type="number" step="0.01" min="0.01"
                            placeholder={`max ${fmtMoney(paymentSummary.rest_de_incasat, doc.moneda)}`}
                            value={incasareForm.valoare}
                            onChange={e => setIF({ valoare: e.target.value })}
                          />
                        </div>
                        <div className="field">
                          <label>Tip</label>
                          <FilterPill value={incasareForm.tip} onChange={e => setIF({ tip: e.target.value })}>
                            {TIP_INCASARE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                          </FilterPill>
                        </div>
                        <div className="field">
                          <label>Data</label>
                          <DateField value={incasareForm.data_incasare} onChange={v => setIF({ data_incasare: v })} />
                        </div>
                      </div>
                      <div className="field">
                        <label>Observatii</label>
                        <input type="text" placeholder="optional" value={incasareForm.observatii} onChange={e => setIF({ observatii: e.target.value })} />
                      </div>
                      <div className="iem-inc-form-actions">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAddingIncasare(false)}>Anuleaza</button>
                        <button type="button" className="btn btn-primary btn-sm" onClick={handleAddIncasare} disabled={savingIncasare}>
                          {savingIncasare ? 'Se salveaza...' : 'Inregistreaza'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    !isStornata && paymentSummary.rest_de_incasat > 0.01 && (
                      <button type="button" className="btn btn-secondary btn-sm iem-inc-add-btn" onClick={() => setAddingIncasare(true)}>
                        + Incasare noua
                      </button>
                    )
                  )}

                  {isStornata && (
                    <div className="iem-storno-badge">
                      Stornata{doc.storno_numar ? ` — factura storno: ${doc.storno_serie}-${doc.storno_numar}` : ''}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="fm-footer">
          {converting ? (
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setConverting(false)}>
                Inapoi
              </button>
              <button type="button" className="btn btn-primary" onClick={handleConvert} disabled={convertSaving}>
                {convertSaving ? 'Se emite...' : 'Converteste'}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Renunta</button>
              {doc.numar && onDuplicate && (
                <button type="button" className="btn btn-secondary" onClick={() => { onDuplicate(doc); onClose(); }}>
                  Duplica
                </button>
              )}
              {isProforma && onReEdit && (
                <button type="button" className="btn btn-secondary" onClick={() => { onReEdit(doc); onClose(); }}>
                  Editeaza
                </button>
              )}
              {isProforma && doc.numar && (
                <button type="button" className="btn btn-secondary" onClick={handleConvertClick}>
                  → Fiscala
                </button>
              )}
              {isFiscala && doc.numar && !isStornata && (
                <button type="button" className="btn btn-danger btn-sm" onClick={handleStorno} disabled={stornoLoading}>
                  {stornoLoading ? 'Se storneaza...' : 'Storneaza'}
                </button>
              )}
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Se salveaza...' : 'Salveaza'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
